// GC4 — wallet balance hook for /api/billing/wallet (the per-user wallet
// introduced in GC2 / PR #81). Separate from useWalletBalance() which hits
// the org-scoped /api/organizations/:id/billing/balance endpoint.

import { useQuery } from '@tanstack/react-query'
import { apiFetch, getAuthToken } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspaceStore'

export interface WalletData {
  org_id: string
  /** Persistent top-up bucket only (legacy field). */
  balance_microusd: number
  balance_usd: string
  /** Canonical spendable = top-up + remaining monthly allowance − reserved.
   *  This is what every balance surface should display. */
  available_microusd: number
  available_usd: string
  monthly_allowance_microusd: number
  monthly_used_microusd: number
  currency: string
  initial_grant_microusd: number
  wallet_exists: boolean
}

export function useWallet() {
  const hasToken = !!getAuthToken()
  // /api/billing/wallet is org-scoped server-side via X-Org-Id — the response
  // even carries org_id — but the key was a bare ['wallet'] with a 60s
  // staleTime, so every org shared one entry. Switching to a team left the
  // sidebar pill showing the personal balance until a navigation, while the
  // roster beside it had already updated. Scoping the key makes that
  // structurally impossible rather than relying on useSwitchOrg's
  // removeQueries() firing first.
  const orgId = useWorkspaceStore((s) => s.activeOrgId)

  const query = useQuery<WalletData>({
    queryKey: ['wallet', orgId],
    queryFn: () => apiFetch<WalletData>('/api/billing/wallet'),
    enabled: hasToken,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry auth/not-found errors — they are expected for signed-out
      // users or accounts without a wallet yet.
      const status = (error as { status?: number })?.status
      if (status === 401 || status === 403 || status === 404) return false
      return failureCount < 2
    },
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}
